import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Label } from '@/shared/components/ui/Label';
import { Textarea } from '@/shared/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/Dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import {
  UserSelectorAvatar,
  UserSelectorItem,
} from '@/shared/components/user-selector';
import { WizardSuccessProps } from '../types/wizard.types';
import {
  DebriefFormData,
  WizardAnswers,
  Employee,
  DebriefInteractionUI,
  Signal,
  Fact,
  TaskIssue,
} from '@momentum/types';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { Trash, Info, Eye } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { DatePickerInput } from '@/shared/components';
import { toast } from 'sonner';

export type EditingSignalsMap = {
  [participantId: string]: {
    [matchPointIndex: number]: (Partial<Signal> | Partial<TaskIssue>)[];
  };
};

interface DebriefReviewStepProps extends Omit<WizardSuccessProps, 'onClose'> {
  answers: WizardAnswers<DebriefFormData>;
  employees: Employee[];
  onSendInteraction?: (editingSignals: EditingSignalsMap) => void;
  onSaveDraft?: (editingSignals: EditingSignalsMap) => void;
  onSaveSignals?: (signals: EditingSignalsMap) => Promise<void>;
  isSubmitting?: boolean;
  existingDebrief?: DebriefInteractionUI;
  onStartBot?: () => void;
  closeDebrief?: () => void;
  onUpdateViewers?: (viewerIds: string[]) => Promise<void>;
}

interface MatchPointData {
  factId: string;
  factTitle: string;
  index: number;
}

interface ParticipantData {
  employee: Employee;
  formData: Partial<WizardAnswers<DebriefFormData>>;
  hasFullData: boolean; // true if they have what-actions data, false if only signals
}

export const DebriefReviewStep: React.FC<DebriefReviewStepProps> = ({
  answers: currentUserAnswers,
  employees,
  onSendInteraction,
  onSaveDraft,
  onSaveSignals,
  isSubmitting = false,
  onReset,
  existingDebrief,
  onStartBot,
  closeDebrief,
  onUpdateViewers,
}) => {
  const { t, i18n } = useTranslation('wizard');
  const [activeMatchPoint, setActiveMatchPoint] = useState(0);
  const [isFactsModalOpen, setIsFactsModalOpen] = useState(false);
  const [isSavingSignals, setIsSavingSignals] = useState(false);
  const [isViewersModalOpen, setIsViewersModalOpen] = useState(false);
  const [selectedViewers, setSelectedViewers] = useState<string[]>([]);
  const [isUpdatingViewers, setIsUpdatingViewers] = useState(false);

  console.log({ existingDebrief });

  useEffect(() => {
    if (existingDebrief?.viewers) {
      setSelectedViewers(existingDebrief.viewers);
    }
  }, [existingDebrief?.viewers]);

  const handleViewerToggle = (employeeId: string) => {
    setSelectedViewers((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleUpdateViewers = async () => {
    if (onUpdateViewers) {
      setIsUpdatingViewers(true);
      try {
        await onUpdateViewers(selectedViewers);
        setIsViewersModalOpen(false);
        toast.success(t('common.messages.viewersUpdatedSuccessfully'));
      } catch (error) {
        toast.error(t('common.messages.failedToUpdateViewers'));
      } finally {
        setIsUpdatingViewers(false);
      }
    }
  };

  // Format the date
  const formatDate = (date: Date | string) => {
    const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  const [editingSignals, setEditingSignals] = useState<{
    [participantId: string]: {
      [matchPointIndex: number]: (Partial<Signal> | Partial<TaskIssue>)[];
    };
  }>({});
  const currentUser = useEmployeeContext();

  const debriefLeaderAnswers =
    existingDebrief?.formData?.[existingDebrief.creatorId] ||
    currentUserAnswers;
  const allEmployeesAnswers = existingDebrief?.formData;

  // Extract match points from the answers
  const matchPoints: MatchPointData[] = useMemo(() => {
    const topFacts = debriefLeaderAnswers['debrief-top-facts'];

    if (!topFacts) return [];

    return topFacts.map((topFact, index) => ({
      factId: index.toString(),
      factTitle:
        debriefLeaderAnswers['debrief-facts']?.find(
          (fact) => fact.id === topFact
        )?.text || '',
      index,
    }));
  }, [debriefLeaderAnswers]);

  // Get all facts and selected fact IDs
  const allFacts: Fact[] = useMemo(() => {
    return debriefLeaderAnswers['debrief-facts'] || [];
  }, [debriefLeaderAnswers]);

  const selectedFactIds: string[] = useMemo(() => {
    return debriefLeaderAnswers['debrief-top-facts'] || [];
  }, [debriefLeaderAnswers]);

  // Get debrief leader
  const debriefLeader = useMemo(() => {
    const leaderId = existingDebrief?.creatorId || currentUser._id;
    return employees.find((emp) => emp._id === leaderId);
  }, [existingDebrief, currentUser, employees]);

  const getParticipantData = useCallback(
    (employee: Employee) => {
      return {
        employee,
        formData:
          currentUser._id === employee._id &&
          Object.keys(currentUserAnswers).length > 0
            ? currentUserAnswers
            : allEmployeesAnswers?.[employee._id] || {},
        hasFullData: false, // Non-crucial participants only add signals
      };
    },
    [currentUserAnswers, allEmployeesAnswers, currentUser]
  );

  // Check if there are unsaved signal changes
  const hasUnsavedSignalChanges = useMemo(() => {
    return Object.keys(editingSignals).length > 0;
  }, [editingSignals]);

  // Check if current user has existing signals in the DB
  const hasExistingSignals = useMemo(() => {
    if (!existingDebrief?.formData?.[currentUser._id]) return false;

    // Check if any match point has signals
    return matchPoints.some((_, index) => {
      const signalKey: keyof DebriefFormData = `debrief-match-point-${index}-signal`;
      const signals = existingDebrief.formData[currentUser._id]?.[signalKey];
      return signals && signals.length > 0;
    });
  }, [existingDebrief, currentUser._id, matchPoints]);

  // Only show save signals button if user has existing signals and there are unsaved changes
  const shouldShowSaveSignalsButton =
    hasExistingSignals &&
    hasUnsavedSignalChanges &&
    !existingDebrief?.metadata?.closedAt;

  // Handle saving signals
  const handleSaveSignals = async () => {
    if (onSaveSignals) {
      setIsSavingSignals(true);
      try {
        await onSaveSignals(editingSignals);
        // Clear editing state after saving
        setEditingSignals({});
      } finally {
        setIsSavingSignals(false);
      }
    }
  };

  // Get crucial participants
  const crucialParticipants: ParticipantData[] = useMemo(() => {
    const crucialIds = debriefLeaderAnswers['debrief-core-participants'];
    if (!crucialIds) return [];

    return crucialIds.map((employee) => getParticipantData(employee));
  }, [getParticipantData, debriefLeaderAnswers]);

  // Get non-crucial participants (those who only add signals)
  const nonCrucialParticipants: ParticipantData[] = useMemo(() => {
    const nonCrucialIds = debriefLeaderAnswers['debrief-other-participants'];
    if (!nonCrucialIds) return [];

    return nonCrucialIds.map((employee) => getParticipantData(employee));
  }, [getParticipantData, debriefLeaderAnswers]);

  // Filter employees for viewers list
  const employeesForViewersList = useMemo(() => {
    const excludedIds = new Set<string>();

    // Add debrief leader
    if (debriefLeader?._id) excludedIds.add(debriefLeader._id);

    // Add core participants
    debriefLeaderAnswers['debrief-core-participants']?.forEach((p) =>
      excludedIds.add(p._id)
    );

    // Add other participants
    debriefLeaderAnswers['debrief-other-participants']?.forEach((p) =>
      excludedIds.add(p._id)
    );

    return employees.filter((emp) => !excludedIds.has(emp._id));
  }, [employees, debriefLeader, debriefLeaderAnswers]);

  // Get signals for a specific participant and match point
  const getSignalsForParticipant = (
    participant: ParticipantData,
    matchPointIndex: number
  ): (Partial<Signal> | Partial<TaskIssue>)[] => {
    if (editingSignals[participant.employee._id]?.[matchPointIndex]) {
      return editingSignals[participant.employee._id]?.[matchPointIndex] || [];
    }

    // Get from answers
    const signalKey: keyof DebriefFormData = `debrief-match-point-${matchPointIndex}-signal`;
    const signals = participant.formData?.[signalKey];
    return signals || [];
  };

  // Update signals for a participant
  const updateSignalsForParticipant = (
    participant: ParticipantData,
    matchPointIndex: number,
    signals: (Partial<Signal> | Partial<TaskIssue>)[]
  ) => {
    setEditingSignals((prev) => ({
      ...prev,
      [participant.employee._id]: {
        ...prev[participant.employee._id],
        [matchPointIndex]: signals,
      },
    }));
  };

  // Add a new signal
  const addSignal = (
    participant: ParticipantData,
    matchPointIndex: number,
    type: 'signal' | 'task'
  ) => {
    const currentSignals = getSignalsForParticipant(
      participant,
      matchPointIndex
    );
    const newItem =
      type === 'signal'
        ? { title: '' }
        : { title: '', dueDate: new Date().toISOString() };

    updateSignalsForParticipant(participant, matchPointIndex, [
      ...currentSignals,
      newItem,
    ]);
  };

  // Update a specific signal
  const updateSignal = (
    participant: ParticipantData,
    matchPointIndex: number,
    signalIndex: number,
    value: string | Partial<TaskIssue> | Partial<Signal>
  ) => {
    const currentSignals = getSignalsForParticipant(
      participant,
      matchPointIndex
    );
    const updatedSignals = [...currentSignals];
    const currentItem = updatedSignals[signalIndex];

    if (!currentItem) return;

    if (typeof value === 'string') {
      updatedSignals[signalIndex] = { ...currentItem, title: value };
    } else {
      updatedSignals[signalIndex] = { ...currentItem, ...value };
    }

    updateSignalsForParticipant(participant, matchPointIndex, updatedSignals);
  };

  // Remove a signal
  const removeSignal = (
    participant: ParticipantData,
    matchPointIndex: number,
    signalIndex: number
  ) => {
    const currentSignals = getSignalsForParticipant(
      participant,
      matchPointIndex
    );
    const updatedSignals = currentSignals.filter(
      (_, index) => index !== signalIndex
    );
    updateSignalsForParticipant(participant, matchPointIndex, updatedSignals);
  };

  // Get form data for a specific match point and participant
  const getMatchPointData = (
    participant: ParticipantData,
    matchPointIndex: number
  ) => {
    const whatActionsKey: keyof DebriefFormData = `debrief-match-point-${matchPointIndex}-what-actions`;
    const whyKey: keyof DebriefFormData = `debrief-match-point-${matchPointIndex}-why`;
    const reasonKey: keyof DebriefFormData = `debrief-match-point-${matchPointIndex}-reason`;
    const assumptionKey: keyof DebriefFormData = `debrief-match-point-${matchPointIndex}-assumption`;

    return {
      whatActions:
        participant.formData?.[whatActionsKey]?.['0']?.split('-').join(' ') +
        ' ' +
        participant.formData?.[whatActionsKey]?.['1']?.split('-').join(' '),
      why: participant.formData?.[whyKey],
      reason: participant.formData?.[reasonKey],
      assumption: participant.formData?.[assumptionKey],
    };
  };

  if (matchPoints.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-blue-600 mb-4">
            {t('debriefQuestions.review.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('common.messages.noMatchPoints')}
          </p>
        </div>
      </div>
    );
  }

  const doesEmployeeDidBot = (participant: ParticipantData) => {
    return !!participant.formData?.[
      `debrief-match-point-${activeMatchPoint}-what-actions`
    ];
  };

  const isResultAchieved =
    debriefLeaderAnswers['debrief-expected-result']?.choice ===
    'achieved-results';

  return (
    <div className="mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2 flex flex-col items-center relative">
        {onUpdateViewers && debriefLeader?._id === currentUser._id && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-0 end-0 text-blue-600 hover:bg-blue-100 rounded-2xl"
                  onClick={() => setIsViewersModalOpen(true)}
                  aria-label={t('debriefQuestions.viewers.selectViewers')}
                >
                  <Eye className="size-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('debriefQuestions.viewers.selectViewers')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <h2
          className={cn(
            'text-2xl font-bold',
            isResultAchieved ? 'text-primary' : 'text-destructive'
          )}
        >
          {t(
            `debriefQuestions.review.${
              isResultAchieved ? 'achievedResult' : 'notAchievedResult'
            }`
          )}
        </h2>

        <h3>
          {t('common.labels.startedAt')}{' '}
          {formatDate(existingDebrief?.metadata.createdAt || new Date())}
        </h3>
        {existingDebrief?.metadata.closedAt && (
          <div className="absolute end-0 top-0 overflow-hidden w-48 h-48 pointer-events-none">
            <div className="absolute end-0 top-0 bg-primary text-white text-xs font-semibold px-6 py-2.5 shadow-lg transform rotate-45 rtl:-rotate-45 translate-x-8 rtl:-translate-x-8 translate-y-8 origin-center">
              <div className="whitespace-nowrap">
                {t('common.labels.closedAt')}{' '}
                {formatDate(existingDebrief.metadata.closedAt)}
              </div>
            </div>
          </div>
        )}
        <p className="text-muted-foreground flex gap-2">
          <span>
            {debriefLeaderAnswers['debrief-expected-result']?.textArea}
          </span>
        </p>
      </div>

      {/* Match Points Tabs */}
      <div className="border-b border-gray-200 flex items-center justify-between gap-4">
        <nav className="space-x-2">
          {matchPoints.map((matchPoint, index) => (
            <button
              key={matchPoint.factId}
              onClick={() => setActiveMatchPoint(index)}
              className={`py-3 px-6 border-b-2 font-semibold text-sm transition-all duration-200 ${
                activeMatchPoint === index
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('debriefQuestions.review.matchPoint', { number: index + 1 })}
            </button>
          ))}
        </nav>
        {/* Facts list */}
        {allFacts.length > 0 && (
          <Dialog open={isFactsModalOpen} onOpenChange={setIsFactsModalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <Info className="size-4" />
                {t('common.labels.viewAllFacts' as 'common.labels.factsList')}
              </Button>
            </DialogTrigger>
            <DialogContent size="lg">
              <DialogHeader>
                <DialogTitle>{t('common.labels.factsList')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {allFacts.map((fact, index) => {
                  const isSelected = selectedFactIds.includes(fact.id);
                  const matchPointIndex = isSelected
                    ? selectedFactIds.indexOf(fact.id)
                    : -1;
                  return (
                    <div
                      key={fact.id}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                            isSelected
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-900">{fact.text}</p>
                          {isSelected && (
                            <p className="text-sm text-blue-700 font-medium mt-2">
                              {t('debriefQuestions.review.matchPoint', {
                                number: matchPointIndex + 1,
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        )}
        <Dialog open={isViewersModalOpen} onOpenChange={setIsViewersModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t('debriefQuestions.viewers.selectViewers' as any)}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[40vh] overflow-y-auto space-y-1">
              {employeesForViewersList.map((emp) => (
                <UserSelectorItem
                  key={emp._id}
                  employee={{
                    id: emp._id,
                    firstName: emp.personalInfo.firstName,
                    lastName: emp.personalInfo.lastName,
                    fullName: `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`,
                    isActive: true,
                    imageUrl: emp.personalInfo.imageUrl,
                    role: emp.role,
                  }}
                  isSelected={selectedViewers.includes(emp._id)}
                  isMultiSelect={true}
                  onToggle={handleViewerToggle}
                />
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setIsViewersModalOpen(false)}
              >
                {t('common.buttons.cancel')}
              </Button>
              <Button
                onClick={handleUpdateViewers}
                disabled={isUpdatingViewers}
              >
                {isUpdatingViewers
                  ? t('common.buttons.saving')
                  : t('common.buttons.update' as any)}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Match Point Description */}
      <div className="bg-blue-50 border border-blue-100 p-5 rounded-xl">
        <p className="font-medium">
          {matchPoints[activeMatchPoint]?.factTitle}
        </p>
      </div>

      {/* Table-like Layout */}
      <div className="overflow-x-auto w-full">
        <div className="flex gap-6 min-w-full">
          {/* Labels Column */}
          <div className="flex flex-col gap-4 mt-30 left-0 bg-transparent">
            <div className="flex items-center justify-end h-[120px] border-b border-gray-100 pb-4">
              <Label className="text-base font-semibold text-gray-700">
                {t('common.labels.whatYouDid')}
              </Label>
            </div>
            <div className="flex items-center justify-end h-[120px] border-b border-gray-100 pb-4">
              <Label className="text-base font-semibold text-gray-700">
                {t('common.labels.whatWasYourAssumption')}
              </Label>
            </div>
            <div className="flex items-center justify-end h-auto min-h-[120px] pt-4">
              <Label className="text-base font-semibold text-gray-700">
                {t('common.labels.signalsLearned')}
              </Label>
            </div>
          </div>

          {/* Participants Columns */}
          <div className="flex grow gap-6">
            {/* Debrief Leader */}
            {debriefLeader && (
              <div className="flex flex-col gap-4 min-w-[320px]">
                {/* Header */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-2 border-blue-200 rounded-2xl p-6 shadow-sm relative">
                  <div className="flex items-center gap-4">
                    <UserSelectorAvatar
                      imageUrl={debriefLeader.personalInfo.imageUrl}
                      fullName={`${debriefLeader.personalInfo.firstName} ${debriefLeader.personalInfo.lastName}`}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-md"
                    />
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">
                        {debriefLeader.personalInfo.firstName}{' '}
                        {debriefLeader.personalInfo.lastName}
                      </h3>
                      <p className="text-sm text-blue-700 font-medium">
                        {t('common.labels.debriefLeader')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* What Actions */}
                <div className="h-[120px] border-b border-gray-100 pb-4">
                  <Textarea
                    value={
                      getMatchPointData(
                        {
                          employee: debriefLeader,
                          formData: debriefLeaderAnswers,
                          hasFullData: true,
                        },
                        activeMatchPoint
                      ).whatActions
                    }
                    readOnly
                    className="h-full resize-none bg-gray-50 border-gray-200"
                    rows={4}
                  />
                </div>

                {/* Assumption */}
                <div className="h-[120px] border-b border-gray-100 pb-4">
                  <Textarea
                    value={
                      getMatchPointData(
                        {
                          employee: debriefLeader,
                          formData: debriefLeaderAnswers,
                          hasFullData: true,
                        },
                        activeMatchPoint
                      ).assumption || ''
                    }
                    readOnly
                    className="h-full resize-none bg-gray-50 border-gray-200"
                    rows={4}
                  />
                </div>

                {/* Signals */}
                <div className="space-y-3 pt-4">
                  {getSignalsForParticipant(
                    getParticipantData(debriefLeader),
                    activeMatchPoint
                  ).map((item, itemIndex) => {
                    const isTask = 'title' in item && 'dueDate' in item;
                    const readOnly =
                      !!existingDebrief?.metadata?.closedAt ||
                      currentUser._id !== debriefLeader?._id;
                    return (
                      <div key={itemIndex} className="flex items-center gap-2">
                        {isTask ? (
                          <div className="flex-1">
                            <Textarea
                              value={(item as TaskIssue).title}
                              readOnly={readOnly}
                              onChange={(e) =>
                                updateSignal(
                                  getParticipantData(debriefLeader),
                                  activeMatchPoint,
                                  itemIndex,
                                  { title: e.target.value }
                                )
                              }
                              placeholder="Enter task..."
                              className={cn(
                                'flex-1 bg-white resize-none min-h-20',
                                existingDebrief?.metadata?.closedAt &&
                                  'pointer-events-none bg-gray-50'
                              )}
                            />
                            <DatePickerInput
                              value={
                                new Date((item as TaskIssue).dueDate ?? '')
                              }
                              onChange={(date: Date | null) =>
                                updateSignal(
                                  getParticipantData(debriefLeader),
                                  activeMatchPoint,
                                  itemIndex,
                                  { dueDate: date?.toISOString() }
                                )
                              }
                              disabled={readOnly}
                              placeholder={t('selectDate', 'Select date')}
                            />
                          </div>
                        ) : (
                          <Textarea
                            value={(item as Signal).title}
                            readOnly={readOnly}
                            onChange={(e) =>
                              updateSignal(
                                getParticipantData(debriefLeader),
                                activeMatchPoint,
                                itemIndex,
                                e.target.value
                              )
                            }
                            placeholder="Enter signal..."
                            className={cn(
                              'flex-1 bg-white resize-none min-h-20',
                              existingDebrief?.metadata?.closedAt &&
                                'bg-gray-50'
                            )}
                          />
                        )}
                        {!existingDebrief?.metadata?.closedAt &&
                          currentUser._id === debriefLeader?._id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                removeSignal(
                                  getParticipantData(debriefLeader),
                                  activeMatchPoint,
                                  itemIndex
                                )
                              }
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash className="size-4" />
                            </Button>
                          )}
                      </div>
                    );
                  })}
                  {!existingDebrief?.metadata?.closedAt &&
                    currentUser._id === debriefLeader?._id && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            addSignal(
                              getParticipantData(debriefLeader),
                              activeMatchPoint,
                              'signal'
                            )
                          }
                          className="flex-1 border-dashed border-gray-300 hover:border-blue-500 hover:text-blue-600"
                        >
                          {t('common.buttons.addSignal')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            addSignal(
                              getParticipantData(debriefLeader),
                              activeMatchPoint,
                              'task'
                            )
                          }
                          className="flex-1 border-dashed border-gray-300 hover:border-blue-500 hover:text-blue-600"
                        >
                          {t('common.buttons.addTask')}
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Crucial & Non crucial Participants */}
            {[...crucialParticipants, ...nonCrucialParticipants].map(
              (participant) => (
                <div
                  key={participant.employee._id}
                  className="flex flex-col gap-4 min-w-[320px]"
                >
                  {/* Header */}
                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <UserSelectorAvatar
                        imageUrl={participant.employee.personalInfo.imageUrl}
                        fullName={`${participant.employee.personalInfo.firstName} ${participant.employee.personalInfo.lastName}`}
                        className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100 shadow-sm"
                      />
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">
                          {participant.employee.personalInfo.firstName}{' '}
                          {participant.employee.personalInfo.lastName}
                        </h3>
                        <p className="text-sm text-gray-600 font-medium">
                          {t('common.labels.teamMember')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {!doesEmployeeDidBot(participant) ? (
                    <div className="flex items-center justify-center h-[400px] bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
                      <p className="text-gray-500 text-center px-6">
                        {t('common.messages.participantNotFilledBot')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* What Actions */}
                      <div className="h-[120px] border-b border-gray-100 pb-4">
                        <Textarea
                          value={
                            getMatchPointData(participant, activeMatchPoint)
                              .whatActions
                          }
                          readOnly
                          className="h-full resize-none bg-gray-50 border-gray-200"
                          rows={4}
                        />
                      </div>

                      {/* Assumption */}
                      <div className="h-[120px] border-b border-gray-100 pb-4">
                        <Textarea
                          value={
                            getMatchPointData(participant, activeMatchPoint)
                              .assumption || ''
                          }
                          readOnly
                          className="h-full resize-none bg-gray-50 border-gray-200"
                          rows={4}
                        />
                      </div>

                      {/* Assumption */}
                      <div className="h-[120px] border-b border-gray-100 pb-4">
                        <Textarea
                          value={
                            getMatchPointData(participant, activeMatchPoint)
                              .assumption || ''
                          }
                          readOnly
                          className="h-full resize-none bg-gray-50 border-gray-200"
                          rows={4}
                        />
                      </div>

                      {/* Signals and Tasks */}
                      <div className="space-y-3 pt-4">
                        {getSignalsForParticipant(
                          participant,
                          activeMatchPoint
                        ).map((item, itemIndex) => {
                          const isTask = 'title' in item && 'dueDate' in item;
                          return (
                            <div
                              key={itemIndex}
                              className="flex items-start gap-2"
                            >
                              {isTask ? (
                                <div className="flex-1">
                                  <Textarea
                                    value={(item as TaskIssue).title}
                                    readOnly={
                                      !!existingDebrief?.metadata?.closedAt ||
                                      currentUser._id !==
                                        participant.employee._id
                                    }
                                    onChange={(e) =>
                                      updateSignal(
                                        participant,
                                        activeMatchPoint,
                                        itemIndex,
                                        { title: e.target.value }
                                      )
                                    }
                                    placeholder="Enter task..."
                                    className={cn(
                                      'flex-1 bg-white',
                                      existingDebrief?.metadata?.closedAt &&
                                        'pointer-events-none bg-gray-50'
                                    )}
                                  />
                                  <DatePickerInput
                                    value={
                                      new Date(
                                        (item as TaskIssue).dueDate ?? ''
                                      )
                                    }
                                    onChange={(date: Date | null) =>
                                      updateSignal(
                                        participant,
                                        activeMatchPoint,
                                        itemIndex,
                                        { dueDate: date?.toISOString() }
                                      )
                                    }
                                    disabled={
                                      !!existingDebrief?.metadata?.closedAt ||
                                      currentUser._id !==
                                        participant.employee._id
                                    }
                                    placeholder={t('selectDate', 'Select date')}
                                  />
                                </div>
                              ) : (
                                <Input
                                  value={(item as Signal).title}
                                  readOnly={
                                    !!existingDebrief?.metadata?.closedAt ||
                                    currentUser._id !== participant.employee._id
                                  }
                                  onChange={(e) =>
                                    updateSignal(
                                      participant,
                                      activeMatchPoint,
                                      itemIndex,
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter signal..."
                                  className={cn(
                                    'flex-1 bg-white',
                                    existingDebrief?.metadata?.closedAt &&
                                      'pointer-events-none bg-gray-50'
                                  )}
                                />
                              )}

                              {!existingDebrief?.metadata?.closedAt &&
                                currentUser._id ===
                                  participant.employee._id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      removeSignal(
                                        participant,
                                        activeMatchPoint,
                                        itemIndex
                                      )
                                    }
                                    className="hover:bg-red-50 hover:text-red-600 h-10"
                                  >
                                    <Trash className="size-4" />
                                  </Button>
                                )}
                            </div>
                          );
                        })}
                        {!existingDebrief?.metadata?.closedAt &&
                          currentUser._id === participant.employee._id && (
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  addSignal(
                                    participant,
                                    activeMatchPoint,
                                    'signal'
                                  )
                                }
                                className="flex-1 border-dashed border-gray-300 hover:border-blue-500 hover:text-blue-600"
                              >
                                {t('common.buttons.addSignal')}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  addSignal(
                                    participant,
                                    activeMatchPoint,
                                    'task'
                                  )
                                }
                                className="flex-1 border-dashed border-gray-300 hover:border-blue-500 hover:text-blue-600"
                              >
                                {t('common.buttons.addTask')}
                              </Button>
                            </div>
                          )}
                      </div>
                    </>
                  )}
                </div>
              )
            )}

            {/* Non-Crucial Participants (Other Person) */}
            {nonCrucialParticipants.length > 0 && (
              <div className="flex flex-col gap-4 min-w-[320px]">
                {/* Header */}
                <div className="bg-white border-2 border-dashed border-gray-300 rounded-2xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ring-2 ring-gray-100 shadow-sm">
                      <span className="text-gray-600 text-xl font-bold">+</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">
                        {t('common.labels.otherPerson')}
                      </h3>
                      <p className="text-sm text-gray-600 font-medium">
                        {t('common.labels.additionalParticipant')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {!existingDebrief?.metadata?.closedAt && (
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-8">
          {onReset && (
            <Button
              variant="outline"
              onClick={onReset}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl border-gray-200 hover:bg-gray-50 font-medium"
            >
              {t('debriefQuestions.review.edit')}
            </Button>
          )}

          {onSaveDraft && (
            <Button
              variant="outline"
              onClick={() => onSaveDraft?.(editingSignals)}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl border-gray-200 hover:bg-gray-50 font-medium"
            >
              {t('debriefQuestions.review.saveDraft')}
            </Button>
          )}

          {shouldShowSaveSignalsButton && onSaveSignals && (
            <Button
              variant="default"
              onClick={handleSaveSignals}
              disabled={isSavingSignals || isSubmitting}
              className="px-6 py-2.5 rounded-xl border-gray-200 font-medium"
            >
              {isSavingSignals
                ? t('common.buttons.saving')
                : t('common.buttons.saveSignals')}
            </Button>
          )}

          {onStartBot && (
            <Button
              variant="outline"
              onClick={onStartBot}
              disabled={doesEmployeeDidBot({
                employee: currentUser,
                formData: currentUserAnswers,
                hasFullData: true,
              })}
              className="px-6 py-2.5 rounded-xl border-blue-200 hover:bg-blue-50 text-blue-600 font-medium"
            >
              {t('common.buttons.startBot')}
            </Button>
          )}

          {onSendInteraction && (
            <Button
              onClick={() => onSendInteraction?.(editingSignals)}
              disabled={
                isSubmitting ||
                (doesEmployeeDidBot({
                  employee: currentUser,
                  formData: currentUserAnswers,
                  hasFullData: true,
                }) &&
                  Object.keys(currentUserAnswers).length === 0)
              }
              className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium"
            >
              {isSubmitting
                ? t('debriefQuestions.review.sending')
                : t('debriefQuestions.review.send')}
            </Button>
          )}
          {closeDebrief &&
            currentUser._id === debriefLeader?._id &&
            existingDebrief?.coreParticipants.every(
              (coreParticpant) => coreParticpant in existingDebrief.formData
            ) && (
              <Button
                variant="default"
                onClick={closeDebrief}
                className="px-6 py-2.5 rounded-xl border-gray-200  font-medium"
              >
                {t('common.buttons.closeDebrief')}
              </Button>
            )}
        </div>
      )}
    </div>
  );
};
